export interface LanguageInfo {
  readonly code: string;
  readonly name: string;
  readonly flag: string;
}

export interface LanguageSourceConfig {
  /** Glob pattern for locale files (e.g., '../locales/*.json') */
  readonly globPattern: string;
  /** Regex pattern to extract language code from file path */
  readonly extractPattern?: RegExp;
  /** Default fallback languages when dynamic loading fails */
  readonly fallbackLanguages?: LanguageInfo[];
}

// Language code to display name and flag mapping
export const LANGUAGE_METADATA: Readonly<Record<string, { readonly name: string; readonly flag: string }>> = {
  en: { name: 'English', flag: '🇺🇸' },
  de: { name: 'Deutsch', flag: '🇩🇪' },
  es: { name: 'Español', flag: '🇪🇸' },
  fr: { name: 'Français', flag: '🇫🇷' },
  it: { name: 'Italiano', flag: '🇮🇹' },
  pt: { name: 'Português', flag: '🇵🇹' },
  nl: { name: 'Nederlands', flag: '🇳🇱' },
  zh: { name: '中文', flag: '🇨🇳' },
  ja: { name: '日本語', flag: '🇯🇵' },
  ko: { name: '한국어', flag: '🇰🇷' },
  ru: { name: 'Русский', flag: '🇷🇺' },
  ar: { name: 'العربية', flag: '🇸🇦' },
  hi: { name: 'हिन्दी', flag: '🇮🇳' },
  tr: { name: 'Türkçe', flag: '🇹🇷' },
  pl: { name: 'Polski', flag: '🇵🇱' },
  sv: { name: 'Svenska', flag: '🇸🇪' },
  no: { name: 'Norsk', flag: '🇳🇴' },
  da: { name: 'Dansk', flag: '🇩🇰' },
  fi: { name: 'Suomi', flag: '🇫🇮' },
  cs: { name: 'Čeština', flag: '🇨🇿' },
  sk: { name: 'Slovenčina', flag: '🇸🇰' },
  hu: { name: 'Magyar', flag: '🇭🇺' },
  ro: { name: 'Română', flag: '🇷🇴' },
  bg: { name: 'Български', flag: '🇧🇬' },
  hr: { name: 'Hrvatski', flag: '🇭🇷' },
  sr: { name: 'Српски', flag: '🇷🇸' },
  sl: { name: 'Slovenščina', flag: '🇸🇮' },
  et: { name: 'Eesti', flag: '🇪🇪' },
  lv: { name: 'Latviešu', flag: '🇱🇻' },
  lt: { name: 'Lietuvių', flag: '🇱🇹' },
  el: { name: 'Ελληνικά', flag: '🇬🇷' },
  he: { name: 'עברית', flag: '🇮🇱' },
  th: { name: 'ไทย', flag: '🇹🇭' },
  vi: { name: 'Tiếng Việt', flag: '🇻🇳' },
  id: { name: 'Bahasa Indonesia', flag: '🇮🇩' },
  ms: { name: 'Bahasa Melayu', flag: '🇲🇾' },
  tl: { name: 'Filipino', flag: '🇵🇭' },
  uk: { name: 'Українська', flag: '🇺🇦' },
  be: { name: 'Беларуская', flag: '🇧🇾' },
  ka: { name: 'ქართული', flag: '🇬🇪' },
  am: { name: 'አማርኛ', flag: '🇪🇹' },
  sw: { name: 'Kiswahili', flag: '🇹🇿' },
  zu: { name: 'isiZulu', flag: '🇿🇦' },
  af: { name: 'Afrikaans', flag: '🇿🇦' },
  is: { name: 'Íslenska', flag: '🇮🇸' },
  mt: { name: 'Malti', flag: '🇲🇹' },
  ga: { name: 'Gaeilge', flag: '🇮🇪' },
  cy: { name: 'Cymraeg', flag: '🏴󠁧󠁢󠁷󠁬󠁳󠁿' },
  eu: { name: 'Euskera', flag: '🏴󠁥󠁳󠁰󠁶󠁿' },
  ca: { name: 'Català', flag: '🏴󠁥󠁳󠁣󠁴󠁿' },
  gl: { name: 'Galego', flag: '🏴󠁥󠁳󠁧󠁡󠁿' },
  br: { name: 'Brezhoneg', flag: '🏴󠁦󠁲󠁢󠁲󠁥󠁿' },
  co: { name: 'Corsu', flag: '🇫🇷' },
  oc: { name: 'Occitan', flag: '🇫🇷' },
  rm: { name: 'Rumantsch', flag: '🇨🇭' },
  lb: { name: 'Lëtzebuergesch', flag: '🇱🇺' },
};

// Default configuration for backward compatibility
export const DEFAULT_LANGUAGE_CONFIG: Readonly<LanguageSourceConfig> = {
  globPattern: '../locales/*.json',
  extractPattern: /\/locales\/([^/]+)\.json$/,
  fallbackLanguages: [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  ],
} as const;

/**
 * Maps a file path to a LanguageInfo object
 * Shared logic extracted to avoid duplication
 * @internal
 */
function mapPathToLanguageInfo(path: string, extractPattern: RegExp): LanguageInfo | null {
  const match = path.match(extractPattern);
  if (!match) return null;

  const languageCode = match[1];
  const metadata = LANGUAGE_METADATA[languageCode];

  return {
    code: languageCode,
    name: metadata?.name ?? languageCode.toUpperCase(),
    flag: metadata?.flag ?? '🌐',
  };
}

/**
 * Creates a language discovery utility with configurable options
 * For library usage with different locale file sources
 * @param localeModules Pre-loaded modules from import.meta.glob (must be passed from consuming code)
 * @param config Configuration options
 * @returns Language discovery utilities
 */
export const createLanguageDiscovery = (
  localeModules: Record<string, () => Promise<unknown>>,
  config?: Omit<LanguageSourceConfig, 'globPattern'>
) => {
  const sourceConfig = {
    ...DEFAULT_LANGUAGE_CONFIG,
    ...config,
  };

  return {
    getAvailableLanguages: async (): Promise<LanguageInfo[]> => {
      try {
        // Load all modules to get the actual files
        const modulePromises = Object.entries(localeModules).map(async ([path, loader]) => {
          await loader(); // Actually load the module
          return path;
        });

        const loadedPaths = await Promise.all(modulePromises);

        const availableLanguages = loadedPaths
          .map((path) => mapPathToLanguageInfo(path, sourceConfig.extractPattern!))
          .filter((lang): lang is LanguageInfo => lang !== null);

        // Sort languages alphabetically by name
        return availableLanguages.sort((a, b) => a.name.localeCompare(b.name));
      } catch (error) {
        // Use fusionApp logger if available, otherwise fallback to console
        if (typeof window !== 'undefined' && window.console) {
          console.warn('Failed to load available languages:', error);
        }
        // Return configured fallback languages
        return sourceConfig.fallbackLanguages || [];
      }
    },

    getAvailableLanguagesSync: (localeFiles: Record<string, unknown>): LanguageInfo[] => {
      try {
        const availableLanguages = Object.keys(localeFiles)
          .map((path) => mapPathToLanguageInfo(path, sourceConfig.extractPattern!))
          .filter((lang): lang is LanguageInfo => lang !== null);

        return availableLanguages.sort((a, b) => a.name.localeCompare(b.name));
      } catch (error) {
        // Use fusionApp logger if available, otherwise fallback to console
        if (typeof window !== 'undefined' && window.console) {
          console.warn('Failed to load available languages synchronously:', error);
        }
        // Return configured fallback languages
        return sourceConfig.fallbackLanguages || [];
      }
    },
  };
};

// Create default discovery instance (using factory to avoid duplication)
const defaultDiscovery = createLanguageDiscovery(import.meta.glob('../locales/*.json'), {
  extractPattern: DEFAULT_LANGUAGE_CONFIG.extractPattern,
});

/**
 * Dynamically discovers available languages based on locale files (default implementation)
 * @returns Promise<LanguageInfo[]> Array of available languages with metadata
 */
/** @public */
export const getAvailableLanguages = defaultDiscovery.getAvailableLanguages;

/**
 * Synchronously get available languages (automatically discovers locale files - default implementation)
 * This is useful for components that need languages immediately
 */
export const getAvailableLanguagesSync = (): LanguageInfo[] => {
  // Use eager import with ?url query to get file paths without importing content
  // This avoids duplication warnings while still being automatic
  const localeFiles = import.meta.glob('../locales/*.json', { eager: true, query: '?url' });

  return defaultDiscovery.getAvailableLanguagesSync(localeFiles);
};

// Note: All exports are already declared above with proper access modifiers
